/**
 * 한글을 자모로 푼다.
 *
 * 사람은 `덕봉빌딩` 이라 말하고 화면은 `덕봉빌딩(두암정철)` 을 요구한다. 글자 단위로만
 * 비교하면 이런 것도, 오타(`덕붕빌딩`)도, 초성(`ㄷㅂㅂㄷ`)도 못 잡는다. 한글 음절은
 * (초성, 중성, 종성) 셋을 한 칸에 눌러 담은 것이라 풀면 그 셋이 다 같은 잣대가 된다.
 *
 * 주소록 검색이 오래 쓰던 방식이다(KR20150015878A). 새로 발명할 것이 없다.
 */

const BASE = 0xac00;
const LAST = 0xd7a3;
const INITIALS = 'ㄱㄲㄴㄷㄸㄹㅁㅂㅃㅅㅆㅇㅈㅉㅊㅋㅌㅍㅎ';
const MEDIALS = 'ㅏㅐㅑㅒㅓㅔㅕㅖㅗㅘㅙㅚㅛㅜㅝㅞㅟㅠㅡㅢㅣ';
const FINALS = ' ㄱㄲㄳㄴㄵㄶㄷㄹㄺㄻㄼㄽㄾㄿㅀㅁㅂㅄㅅㅆㅇㅈㅊㅋㅌㅍㅎ';

/** 비교에 쓸 글자만 남긴다. 괄호·공백·기호는 표기 차이일 뿐이다. */
export function normalize(value: string): string {
  return value.replace(/[^0-9A-Za-z가-힣ㄱ-ㅎㅏ-ㅣ]/g, '').toLocaleLowerCase();
}

/** 음절을 자모 열로 푼다. 한글이 아닌 글자는 그대로 둔다. */
export function toJamo(value: string): string {
  let out = '';
  for (const character of normalize(value)) {
    const code = character.charCodeAt(0);
    if (code < BASE || code > LAST) { out += character; continue; }
    const index = code - BASE;
    out += INITIALS[Math.floor(index / 588)];
    out += MEDIALS[Math.floor((index % 588) / 28)];
    const final = FINALS[index % 28];
    if (final !== ' ') out += final;
  }
  return out;
}

/** 초성만 뽑는다. `덕봉빌딩` → `ㄷㅂㅂㄷ`. */
export function initials(value: string): string {
  let out = '';
  for (const character of normalize(value)) {
    const code = character.charCodeAt(0);
    if (code >= BASE && code <= LAST) { out += INITIALS[Math.floor((code - BASE) / 588)]; continue; }
    if (INITIALS.includes(character)) { out += character; continue; }
    out += character;
  }
  return out;
}

/** 세 글자씩 끊은 조각. 짧은 말은 통째로 한 조각이 된다. */
function trigrams(value: string): string[] {
  if (value.length <= 3) return value ? [value] : [];
  const out: string[] = [];
  for (let index = 0; index + 3 <= value.length; index += 1) out.push(value.slice(index, index + 3));
  return out;
}

/** 겹치는 조각의 비율. 짧은 쪽을 기준으로 잰다 — 사람은 이름을 줄여 말한다. */
function overlap(one: string, other: string): number {
  const [left, right] = [trigrams(one), trigrams(other)];
  if (!left.length || !right.length) return 0;
  const pool = new Set(right);
  const hit = left.filter((piece) => pool.has(piece)).length;
  return hit / Math.min(left.length, right.length);
}

/**
 * 사람이 말한 이름과 화면의 이름이 얼마나 같은가. 0~1.
 *
 * 순서대로: 같은 이름 → 앞에서부터 같음 → 통째로 들어 있음 → 자모가 겹침 → 초성이 같음.
 * 뒤로 갈수록 약한 근거라 점수도 낮다. 부르는 쪽이 **마진**을 함께 보고 결정한다.
 */
export function similarity(spoken: string, candidate: string): number {
  const [said, shown] = [normalize(spoken), normalize(candidate)];
  if (!said || !shown) return 0;
  if (said === shown) return 1;
  if (shown.startsWith(said) || said.startsWith(shown)) return 0.9;
  if (shown.includes(said) || said.includes(shown)) return 0.8;
  const jamo = overlap(toJamo(said), toJamo(shown));
  if (jamo >= 0.6) return 0.5 + jamo * 0.25;
  const chosung = initials(said);
  if (chosung.length >= 2 && initials(shown).includes(chosung)) return 0.55;
  return jamo * 0.5;
}

/**
 * 후보 중 하나를 고른다. **1위가 뚜렷할 때만.**
 *
 * 자동 확정은 두 조건을 같이 본다 — 1위가 임계값을 넘고, 2위와 충분히 벌어졌을 때.
 * 둘이 비슷하면 고르지 않는다. 그 판단은 화면을 보는 사람 몫이다.
 */
export function bestMatch<T>(
  spoken: string,
  candidates: T[],
  nameOf: (candidate: T) => string,
  options: { floor?: number; margin?: number } = {},
): { chosen?: T; ranked: { candidate: T; score: number }[] } {
  const floor = options.floor ?? 0.55;
  const margin = options.margin ?? 0.15;
  const ranked = candidates
    .map((candidate) => ({ candidate, score: similarity(spoken, nameOf(candidate)) }))
    .sort((left, right) => right.score - left.score);
  const [first, second] = ranked;
  if (!first || first.score < floor) return { ranked };
  if (second && first.score - second.score < margin) return { ranked };
  return { chosen: first.candidate, ranked };
}
